import { ChangeEvent, CSSProperties, useEffect, useMemo, useState } from "react";
import { classNames, useIsMounted } from "@ct-react/core";
import { useElementSize } from "../../hooks/dom";
import "./slider.scss";

type Config = {
  min: number;
  max: number;
  step: number;
  limitsAsUndefined: boolean;
  labelize?: (val: number) => string;
}

type Props = {
  className?: string;
  config: Config,
  value?: [ number, number ],
  onChange?: (value?: [number, number]) => void,
}

const SliderInput = (
  {
    className,
    config,
    value,
    onChange = () => void 0
  }: Props) => {

  const isMounted = useIsMounted();
  const [ edition, setEdition ] = useState<[ number, number ]>(value || [ config.min, config.max ]);

  const { ref: minRef, size: minSize } = useElementSize();
  const { ref: maxRef, size: maxSize } = useElementSize();

  const positionProperties = useMemo<CSSProperties>(() => {
    return {
      "--ccv-slider-pcalc-from": (edition[0] - config.min) / config.step,
      "--ccv-slider-pcalc-to": (edition[1] - config.min) / config.step,
      "--ccv-slider-pcalc-max": (config.max - config.min) / config.step,
    } as CSSProperties;
  }, [ config, edition ]);

  useEffect(() => setEdition(value || [ config.min, config.max ]), [ value ]);

  useEffect(() => {
    if (!isMounted) return;
    onChange((config.limitsAsUndefined && edition[0] === config.min && edition[1] === config.max) ? undefined : edition);
  }, [ edition ]);

  const onInputValueChange = (type: "min" | "max", e: ChangeEvent<HTMLInputElement>) => {
    if (type === "min")
      setEdition([ Math.min(+e.target.value, edition[1] - config.step), edition[1] ]);
    else
      setEdition([ edition[0], Math.max(+e.target.value, edition[0] + config.step) ]);
  }

  const wrapperClasses = classNames("slider-input", className);

  return (
    <div className={wrapperClasses}>
      <div className="slider"
           style={positionProperties}>
        <div className="track" />
        <div className="range" />
        <input type="range"
               className="thumb"
               min={config.min}
               max={config.max}
               step={config.step}
               value={edition[0]}
               onChange={onInputValueChange.bind(this, "min")} />
        <input type="range"
               className="thumb"
               min={config.min}
               max={config.max}
               step={config.step}
               value={edition[1]}
               onChange={onInputValueChange.bind(this, "max")} />
        <span ref={minRef}
              className="value min"
              style={{ "--ccv-slider-pcalc-self-width": `${minSize?.width}px` } as CSSProperties}>
          {!!config.labelize ? config.labelize(edition[0]) : edition[0]}
        </span>
        <span ref={maxRef}
              className="value max"
              style={{ "--ccv-slider-pcalc-self-width": `${maxSize?.width}px` } as CSSProperties}>
          {!!config.labelize ? config.labelize(edition[1]) : edition[1]}
        </span>
      </div>
    </div>);

}

export default SliderInput;
